import { useEffect, useMemo, useState } from "react";
import { Download, Smartphone, X, AlertTriangle, Share } from "lucide-react";

/**
 * InstallPWAButton — Discreet "Install app" entry point.
 *
 * Platform matrix:
 *  • Android Chrome / Edge / Desktop Chromium: native beforeinstallprompt → ONE-CLICK install.
 *    (We call e.preventDefault() in index.html to capture the event, then evt.prompt() on click.)
 *  • Safari iOS (iPhone portrait): instructions pointing to the Share icon in the BOTTOM toolbar.
 *  • Safari iPadOS: instructions pointing to the Share icon in the TOP toolbar.
 *  • Chrome / Firefox / in-app browser on iOS: cannot install → explicit message asking to open in Safari.
 *  • Already installed (standalone): component renders nothing.
 */

// ---- Platform detection ------------------------------------------------------
const detectPlatform = () => {
  if (typeof navigator === "undefined") {
    return { os: "unknown", browser: "unknown", form: "unknown", canInstalliOS: false };
  }
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";

  // iPadOS 13+ reports as "MacIntel" but with touch points > 1
  const iPadOSTrick =
    platform === "MacIntel" && typeof navigator.maxTouchPoints === "number" && navigator.maxTouchPoints > 1;

  const isIPhone = /iPhone/.test(ua);
  const isIPad = /iPad/.test(ua) || iPadOSTrick;
  const isIPod = /iPod/.test(ua);
  const isIOS = isIPhone || isIPad || isIPod;
  const isAndroid = /Android/.test(ua);

  // Browser (iOS browsers all use WebKit but expose markers in UA)
  const isCriOS = /CriOS/.test(ua); // Chrome iOS
  const isFxiOS = /FxiOS/.test(ua); // Firefox iOS
  const isEdgiOS = /EdgiOS/.test(ua); // Edge iOS
  const isOperaiOS = /OPT\//.test(ua);
  const isInApp =
    /FBAN|FBAV|FBIOS|Instagram|Line\/|LinkedInApp|GSA\/|Twitter|TikTok|Pinterest|Snapchat|GmailApp/i.test(ua);

  const isSafari =
    isIOS && !isCriOS && !isFxiOS && !isEdgiOS && !isOperaiOS && !isInApp;

  const os = isIOS ? "ios" : isAndroid ? "android" : "desktop";
  const form = isIPad ? "ipad" : isIPhone ? "iphone" : isIPod ? "ipod" : os === "android" ? "android" : "desktop";

  let browser = "other";
  if (isSafari) browser = "safari";
  else if (isCriOS) browser = "chrome-ios";
  else if (isFxiOS) browser = "firefox-ios";
  else if (isEdgiOS) browser = "edge-ios";
  else if (isInApp) browser = "in-app";

  // On iOS, "Add to Home Screen" is only available in Safari.
  const canInstalliOS = isIOS && isSafari;

  return { os, browser, form, canInstalliOS, isIOS, isAndroid, isSafari };
};

const isStandalone = () => {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
};

// ---- Component ---------------------------------------------------------------
export const InstallPWAButton = () => {
  const [canPrompt, setCanPrompt] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const [installed, setInstalled] = useState(false);
  const platform = useMemo(detectPlatform, []);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    const onAvailable = () => setCanPrompt(true);
    const onInstalled = () => {
      setCanPrompt(false);
      setInstalled(true);
    };
    window.addEventListener("rp-install-available", onAvailable);
    window.addEventListener("rp-install-done", onInstalled);
    if (window.__rpInstallPromptEvent) setCanPrompt(true);
    return () => {
      window.removeEventListener("rp-install-available", onAvailable);
      window.removeEventListener("rp-install-done", onInstalled);
    };
  }, []);

  if (installed) return null;

  // When to show the button:
  //  - canPrompt (native Android/Chromium support) → yes
  //  - iOS → yes (we'll show instructions)
  //  - otherwise (desktop Firefox etc.) → hide
  const shouldShow = canPrompt || platform.isIOS;
  if (!shouldShow) return null;

  const handleClick = async () => {
    if (platform.isIOS) {
      setShowIOSHelp(true);
      return;
    }
    const evt = window.__rpInstallPromptEvent;
    if (!evt) return;
    evt.prompt();
    try {
      await evt.userChoice;
    } catch (_) {}
    window.__rpInstallPromptEvent = null;
    setCanPrompt(false);
  };

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        data-testid="footer-install-pwa"
        className="inline-flex items-center text-sm text-neutral-700 hover:text-[var(--red)] transition-colors"
      >
        <Download className="inline h-3.5 w-3.5 mr-1.5" />
        Installer l'app
      </button>

      {showIOSHelp && (
        <IOSInstallDialog platform={platform} onClose={() => setShowIOSHelp(false)} />
      )}
    </>
  );
};

// ---- iOS Dialog --------------------------------------------------------------
const IOSInstallDialog = ({ platform, onClose }) => {
  const canInstall = platform.canInstalliOS;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="ios-install-help"
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-sm rounded-t-2xl sm:rounded-2xl shadow-xl p-6 relative"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Fermer"
          className="absolute top-3 right-3 text-neutral-500 hover:text-neutral-900"
        >
          <X className="h-5 w-5" />
        </button>

        {!canInstall ? (
          <WrongBrowserMessage platform={platform} />
        ) : platform.form === "ipad" ? (
          <IPadInstructions />
        ) : (
          <IPhoneInstructions />
        )}
      </div>
    </div>
  );
};

const SectionHeader = ({ icon: Icon, label }) => (
  <div className="flex items-center gap-2 mb-3 text-[var(--red)]">
    <Icon className="h-5 w-5" />
    <span className="text-[11px] uppercase tracking-[0.2em] font-medium">{label}</span>
  </div>
);

const IPhoneInstructions = () => (
  <>
    <SectionHeader icon={Smartphone} label="Installation iPhone" />
    <h3 className="font-semibold text-lg mb-3">Ajoutez Rouge Première à l'écran d'accueil</h3>
    <ol className="text-sm text-neutral-700 space-y-2.5 list-decimal pl-5">
      <li>
        Appuyez sur l'icône <strong>Partager</strong>{" "}
        <span className="inline-flex items-center align-middle mx-0.5">
          <Share className="h-4 w-4" />
        </span>
        — le carré avec une flèche vers le haut.
        <div className="text-xs text-neutral-500 mt-1">
          Généralement <strong>en bas de l'écran</strong> (en mode portrait).
          Si vous ne la voyez pas, touchez d'abord la barre d'adresse en bas pour faire
          réapparaître les icônes.
        </div>
      </li>
      <li>
        Faites défiler et choisissez <strong>Sur l'écran d'accueil</strong>.
      </li>
      <li>
        Appuyez sur <strong>Ajouter</strong> en haut à droite.
      </li>
    </ol>
    <p className="text-xs text-neutral-500 mt-4">
      L'app s'ouvrira ensuite en plein écran, sans la barre du navigateur.
    </p>
  </>
);

const IPadInstructions = () => (
  <>
    <SectionHeader icon={Smartphone} label="Installation iPad" />
    <h3 className="font-semibold text-lg mb-3">Ajoutez Rouge Première à l'écran d'accueil</h3>
    <ol className="text-sm text-neutral-700 space-y-2.5 list-decimal pl-5">
      <li>
        Appuyez sur l'icône <strong>Partager</strong>{" "}
        <span className="inline-flex items-center align-middle mx-0.5">
          <Share className="h-4 w-4" />
        </span>
        , située <strong>en haut à droite</strong>, à côté de la barre d'adresse.
      </li>
      <li>
        Faites défiler et choisissez <strong>Sur l'écran d'accueil</strong>.
      </li>
      <li>
        Appuyez sur <strong>Ajouter</strong>.
      </li>
    </ol>
    <p className="text-xs text-neutral-500 mt-4">
      L'app s'ouvrira ensuite en plein écran, sans la barre du navigateur.
    </p>
  </>
);

const WrongBrowserMessage = ({ platform }) => {
  const browserLabel =
    platform.browser === "chrome-ios"
      ? "Chrome"
      : platform.browser === "firefox-ios"
        ? "Firefox"
        : platform.browser === "edge-ios"
          ? "Edge"
          : platform.browser === "in-app"
            ? "le navigateur intégré d'une app (Instagram, Facebook, Gmail…)"
            : "ce navigateur";

  return (
    <>
      <SectionHeader icon={AlertTriangle} label="Ouvrez d'abord dans Safari" />
      <h3 className="font-semibold text-lg mb-3">
        L'installation nécessite Safari sur iPhone / iPad
      </h3>
      <p className="text-sm text-neutral-700 leading-relaxed mb-3">
        Vous consultez actuellement Rouge Première depuis <strong>{browserLabel}</strong>.
        Apple ne permet l'ajout à l'écran d'accueil que depuis <strong>Safari</strong>.
      </p>
      <ol className="text-sm text-neutral-700 space-y-2 list-decimal pl-5">
        <li>Ouvrez Safari sur votre iPhone / iPad.</li>
        <li>
          Allez sur <strong>rougepremiere.com</strong>.
        </li>
        <li>
          Rouvrez ce menu : il affichera les étapes pour ajouter l'app à l'écran d'accueil.
        </li>
      </ol>
    </>
  );
};

export default InstallPWAButton;
